import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PublicNavbar from "../components/PublicNavbar";
import LoginModal from "../components/LoginModal";
import Footer from "../components/Footer";
import { usePWAInstall } from '../App';
import { announcementAPI } from "../services/announcementAPI";
import "./Login.css";

function Login() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [publicAnnouncement, setPublicAnnouncement] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const { installPrompt, handleInstallClick } = usePWAInstall();

  useEffect(() => {
    const reason = searchParams.get("reason");
    if (reason === "account-deleted") {
      setInfoMessage(
        "Your account has been deleted. Please contact support if you believe this is an error.",
      );
    } else if (reason === "session-expired") {
    }
  }, [searchParams]);

  useEffect(() => {
    announcementAPI.getPublic()
      .then(res => setPublicAnnouncement(res.data[0] || null))
      .catch(() => setPublicAnnouncement(null));
  }, []);

  return (
    <>
      <PublicNavbar onSignInClick={() => setIsModalOpen(true)} />

      <div className="login-page">
        <div className="login-container">
          {infoMessage && <div className="info-banner">{infoMessage}</div>}

          {/* ── SECTION 1: HERO ── */}
          <div className="login-hero">
            <h1 className="hero-title">
              Your Player. Your Data. Your Edge.
            </h1>
            <p className="hero-subtitle">
              The only player development tool built for the parent in the stands, not the coach in the dugout.
            </p>
            <p className="hero-description">
              Track your player's stats from the stands. Discover trends before anyone else notices. And when you want answers, just ask.
            </p>
            <div className="hero-journey">
              <div className="hero-journey-step">
                <span className="hero-journey-word">TRACK</span>
                <span className="hero-journey-desc">Every at-bat, pitch, and play</span>
              </div>
              <div className="hero-journey-arrow">→</div>
              <div className="hero-journey-step">
                <span className="hero-journey-word">UNDERSTAND</span>
                <span className="hero-journey-desc">Trends and insights from your data</span>
              </div>
              <div className="hero-journey-arrow">→</div>
              <div className="hero-journey-step">
                <span className="hero-journey-word">DIG DEEPER</span>
                <span className="hero-journey-desc">Ask anything, get real answers</span>
              </div>
            </div>
            <button
              className="btn btn-primary hero-cta"
              onClick={() => navigate("/register")}
              style={{
                background: "#ffffff",
                border: "2px solid #ffffff",
                color: "#c0001a",
              }}
            >
              Start Tracking for Free
            </button>
            <p className="hero-no-cc">No credit card required</p>
            {installPrompt && (
              <button
                className="pwa-install-nudge"
                onClick={handleInstallClick}
              >
                📲 Add Playmaker to your home screen
              </button>
            )}
          </div>

          {publicAnnouncement && (
            <div className="public-announcement">
              <div className="public-announcement-text">
                {publicAnnouncement.message}
              </div>
              {publicAnnouncement.linkUrl && publicAnnouncement.linkText && (
                <a
                  href={publicAnnouncement.linkUrl}
                  className="public-announcement-link"
                >
                  {publicAnnouncement.linkText}
                </a>
              )}
            </div>
          )}

          {/* ── TRUST BAR ── */}
          <div className="trust-bar">
            <div className="trust-pill">✓ Built for Baseball & Softball</div>
            <div className="trust-pill">✓ Real Game Data</div>
            <div className="trust-pill">✓ Development Insights</div>
            <div className="trust-pill">✓ Shareable BragCards</div>
          </div>

          {/* ── SECTION 2: DIFFERENTIATOR ── */}
          <div className="lp-section lp-section-navy">
            <div className="lp-section-content lp-centered">
              <div className="lp-eyebrow">Why Playmaker</div>
              <h2 className="lp-heading">Built for Parents. Not Coaches.</h2>
              <p className="lp-body lp-body-large">
                Most stat apps are run by the coach. You see what they enter,
                when they enter it, if they enter it at all.
              </p>
              <p className="lp-body lp-body-large">
                Playmaker is different. You track your player yourself. Every
                at-bat, every pitch, every game. Your eyes, your notes, your
                data. No padded stats. No missing data. No waiting on someone
                else. The truth about where your player stands. And exactly
                what they need to work on.
              </p>
              <p className="lp-differentiator-closer">
                This is just for you and your kid. And that changes everything.
              </p>
            </div>
          </div>

          {/* ── CHAPTER MARKER: TRACK ── */}
          <div className="lp-chapter-marker">
            <div className="lp-chapter-line" />
            <span className="lp-chapter-word">TRACK</span>
            <div className="lp-chapter-line" />
          </div>

          {/* ── SECTION 3: TRACKER ── */}
          <div className="lp-section lp-section-dark">
            <div className="lp-section-content lp-split">
              <div className="lp-split-text">
                <div className="lp-eyebrow">Live Game Tracker</div>
                <h2 className="lp-heading">This Is Where It Starts</h2>
                <p className="lp-body">
                  You're not the scorekeeper. You're a parent. Track your player, not the whole team. Every at-bat, pitch, and play live. Batting, pitching, and fielding all in one place. Your player's complete story, game by game.
                </p>
                <p className="lp-body">
                  And it's simpler than you think. Only open Playmaker when your kid steps up to bat, takes the mound, or makes a play in the field. Everything else? Put your phone down and just watch.
                </p>
                <ul className="lp-list">
                  <li>Built for baseball and softball parents tracking from the stands</li>
                  <li>Batting, pitching, and fielding all tracked in real time</li>
                  <li>Save each game to your player's profile in seconds</li>
                  <li>Works on any device, anywhere in the ballpark</li>
                </ul>
              </div>
              <div className="lp-split-visual">
                <img
                  src="/Tracker_Screenshot.jpg"
                  alt="Playmaker live game tracker on mobile"
                  style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                />
              </div>
            </div>
          </div>

          {/* ── CHAPTER MARKER: UNDERSTAND ── */}
          <div className="lp-chapter-marker" style={{ background: '#1a1e2e' }}>
            <div className="lp-chapter-line" />
            <span className="lp-chapter-word">UNDERSTAND</span>
            <div className="lp-chapter-line" />
          </div>

          {/* ── SECTION 4: DEVELOPMENT INSIGHTS ── */}
          <div className="lp-section lp-section-navy">
            <div className="lp-section-content lp-split lp-split-reverse">
              <div className="lp-split-text">
                <div className="lp-eyebrow">Development Insights</div>
                <h2 className="lp-heading">Stop Guessing. Start Developing.</h2>
                <p className="lp-body">
                  Every parent watches their kid play and wonders: are they
                  getting better? Where are they struggling? What should they be
                  working on? Playmaker answers those questions with real data
                  from real games you tracked yourself.
                </p>
                <ul className="lp-list">
                  <li>Season stats at a glance: AVG, OBP, ERA, WHIP, strikeout rate and more</li>
                  <li>See how your player stacks up against others at their level</li>
                  <li>Spot hot streaks, slumps, and plate discipline trends before anyone else does</li>
                  <li>Catch signs of fatigue and overuse before they become a problem</li>
                  <li>The more you track, the sharper the insights get</li>
                </ul>
              </div>
              <div className="lp-split-visual">
                <img
                  src="/dashboard_screenshot.jpg"
                  alt="Playmaker analytics dashboard showing player stats and insights"
                  style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                />
              </div>
            </div>
          </div>

          {/* ── CHAPTER MARKER: DIG DEEPER ── */}
          <div className="lp-chapter-marker">
            <div className="lp-chapter-line" />
            <span className="lp-chapter-word">DIG DEEPER</span>
            <div className="lp-chapter-line" />
          </div>

          {/* ── SECTION 5: ASK THE ANALYST ── */}
          <div className="lp-section lp-section-dark">
            <div className="lp-section-content lp-split">
              <div className="lp-split-text">
                <div className="lp-eyebrow">Ask the Analyst</div>
                <h2 className="lp-heading">Dig Deeper When You Want Answers</h2>
                <p className="lp-body">
                  Development Insights show you the trends. Ask the Analyst to find out why. Type any question about your player's game and get a real answer based on their actual numbers.
                </p>
                <p className="lp-body lp-ask-examples">
                  "Why is his strikeout rate climbing?" "Is her walk rate improving?" "What should we be working on this offseason?"
                </p>
                <p className="lp-body">
                  No guessing. No generic advice. Just answers built around your player's real data.
                </p>
                <ul className="lp-list">
                  <li>Ask anything about your player's stats and development</li>
                  <li>Get personalized responses based on games you actually tracked</li>
                  <li>Available on Pro</li>
                </ul>
              </div>
              <div className="lp-split-visual">
                <img
                  src="/analyst_screenshot.jpg"
                  alt="Ask the Analyst responding to a parent question about their player's batting"
                  style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                />
              </div>
            </div>
          </div>

          {/* ── SECTION 6: BRAGCARDS ── */}
          <div className="lp-section lp-section-navy">
            <div className="lp-section-content lp-split lp-split-reverse">
              <div className="lp-split-text">
                <div className="lp-eyebrow">BragCards</div>
                <h2 className="lp-heading">The Brag They've Earned</h2>
                <p className="lp-body">
                  One dominant outing deserves more than a text in the group
                  chat. When your player has a big game, turn the stats you tracked into a moment worth sharing. In seconds.
                </p>
                <ul className="lp-list">
                  <li>Real game stats turned into something worth posting</li>
                  <li>Add their photo, customize the design, share anywhere</li>
                  <li>Instagram, Facebook, X and more. One tap.</li>
                </ul>
              </div>
              <div className="lp-split-visual">
                <img
                  src="/bragcard_screenshot.jpg"
                  alt="Playmaker BragCard showing player stats ready to share"
                  style={{ width: '100%', borderRadius: '12px', display: 'block' }}
                />
              </div>
            </div>
          </div>

          {/* ── SECTION 7: GRAPHICS ── */}
          <div className="lp-section lp-section-dark">
            <div className="lp-section-content lp-centered">
              <div className="lp-eyebrow">For Coaches & Orgs</div>
              <h2 className="lp-heading">
                Coaches and Orgs: We've Got You Too
              </h2>
              <p className="lp-body">
                Free baseball and softball graphics for game previews, recaps,
                and tryout announcements. Professional quality, no design skills
                needed. Free for everyone. Pro unlocks watermark removal, logo uploads, and background cutouts.
              </p>
              <div className="lp-graphic-tags">
                <span className="lp-tag">Game Previews</span>
                <span className="lp-tag">Game Recaps</span>
                <span className="lp-tag">Tryout Announcements</span>
                <span className="lp-tag">Player Spotlights</span>
              </div>
              <div className="examples-carousel-wrapper">
                <div className="examples-carousel">
                  <div className="example-card">
                    <img src="/examples/preview_example.png" alt="Game Preview Example" loading="lazy" />
                    <h3>Game Previews</h3>
                    <p>Build hype before the big game</p>
                  </div>
                  <div className="example-card">
                    <img src="/examples/recap_example.png" alt="Game Recap Example" loading="lazy" />
                    <h3>Game Recaps</h3>
                    <p>Celebrate wins & highlight players</p>
                  </div>
                  <div className="example-card">
                    <img src="/examples/tryout_example.png" alt="Tryout Example" loading="lazy" />
                    <h3>Tryout Announcements</h3>
                    <p>Professional recruitment graphics</p>
                  </div>
                  <div className="example-card">
                    <img src="/examples/spotlight_example.png" alt="Player Spotlight Example" loading="lazy" />
                    <h3>Player Spotlights</h3>
                    <p>Showcase star players professionally</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION 8: PRICING ── */}
          <div className="lp-section lp-section-crimson">
            <div className="lp-section-content lp-centered">
              <div className="lp-eyebrow">Pricing</div>
              <h2 className="lp-heading">
                Free to Start. Upgrade When You're Ready.
              </h2>
              <p className="lp-body">
                Everything you need to start tracking is completely free. No
                credit card. No time limit. Upgrade when you want more.
              </p>
              <div className="lp-pricing-table">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Free</th>
                      <th>
                        Pro <span className="lp-price">$7.99/mo</span>
                        <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.75, marginTop: 2 }}>or $79/yr</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Stat Tracking & Game History</td>
                      <td>✓</td>
                      <td>✓</td>
                    </tr>
                    <tr>
                      <td>BragCards</td>
                      <td style={{ fontSize: 12 }}>10 cards</td>
                      <td style={{ fontSize: 12 }}>50 cards</td>
                    </tr>
                    <tr>
                      <td>All Graphics Templates</td>
                      <td style={{ fontSize: 12 }}>✓ (watermark)</td>
                      <td style={{ fontSize: 12 }}>✓ (no watermark)</td>
                    </tr>
                    <tr>
                      <td>Benchmark Comparisons</td>
                      <td>—</td>
                      <td>✓</td>
                    </tr>
                    <tr>
                      <td>Development Insights</td>
                      <td>—</td>
                      <td>✓</td>
                    </tr>
                    <tr>
                      <td>Ask the Analyst</td>
                      <td>—</td>
                      <td>20 messages/mo</td>
                    </tr>
                    <tr>
                      <td>Watermark Free</td>
                      <td>—</td>
                      <td>✓</td>
                    </tr>
                    <tr>
                      <td>Background Removal & Cutouts</td>
                      <td>—</td>
                      <td>✓</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ textAlign: 'center', fontSize: 13, opacity: 0.75, marginBottom: 12, color: '#ffffff' }}>
                Need more? <span style={{ fontWeight: 600 }}>Premium</span> starts at $15.99/mo, unlimited everything plus video highlights coming soon.
              </div>
              <button
                className="lp-pricing-link"
                onClick={() => navigate(user ? "/membership" : "/pricing")}
              >
                View Full Pricing →
              </button>
            </div>
          </div>

          {/* ── SECTION 9: FINAL CTA ── */}
          <div className="cta-section">
            <h2>Your Player's Story Starts Here</h2>
            <p>Join free today. Track your first game tonight.</p>
            <button
              className="btn btn-primary hero-cta"
              onClick={() => navigate("/register")}
              style={{
                background: "#c0001a",
                border: "2px solid rgba(255,255,255,0.5)",
              }}
            >
              Create Your Free Account
            </button>
            <p className="hero-no-cc">
              Takes less than 60 seconds. No credit card required.
            </p>
            <p className="cta-signin">
              Already have an account?{" "}
              <button
                className="link-button"
                onClick={() => setIsModalOpen(true)}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <LoginModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export default Login;
